"use client"

import { createBrowserClient } from "./supabase/client"
import { useAuthStore } from "./auth-store"

interface FeatureFlag {
  id: string
  name: string
  enabled: boolean
  rollout_percentage: number
  rollout_type: "percentage" | "user_list" | "group"
  target_user_ids: string[]
}

let flagsCache: FeatureFlag[] = []
let lastFetch = 0
// One shared in-flight request. Without this, every component that asked for a flag during
// startup (before the first response filled the cache) ran its own query — 12 identical
// feature_flags fetches in the same millisecond on a normal page load.
let inflight: Promise<FeatureFlag[]> | null = null
const CACHE_DURATION = 60000 // 1 minute
const DEFAULT_FEATURE_FLAG_ENABLED: Record<string, boolean> = {
  goonpals: true,
  goonpals_coming_soon: false,
  // Glymtober is already date-gated in code (lib/glymtober.ts) — defaulting
  // this on means it works the moment October starts, with no admin setup
  // required, while still leaving a real off-switch in the DB if needed.
  glymtober_modal_enabled: true,
  // Holiday popups (Glymsgiving, Glymus, New Year's: lib/holiday-events.ts) stay OFF until the owner switches this on in Admin > Feature Flags.
  // The boosted daily Gooncoin bonus does NOT depend on this flag; it follows the dates.
  holiday_modal_enabled: false,
}

export function clearFeatureFlagsCache() {
  flagsCache = []
  lastFetch = 0
  inflight = null
}

export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  const now = Date.now()
  // lastFetch > 0 (not flagsCache.length) so a table that is legitimately empty is cached too.
  if (lastFetch > 0 && now - lastFetch < CACHE_DURATION) {
    return flagsCache
  }
  if (inflight) return inflight

  inflight = (async () => {
    const supabase = createBrowserClient()
    const { data } = await supabase.from("feature_flags").select("*")
    if (data) {
      flagsCache = data
      lastFetch = Date.now()
      return data
    }
    return []
  })().finally(() => {
    inflight = null
  })
  return inflight
}

export async function isFeatureEnabled(featureName: string, userId?: string, role?: string | null): Promise<boolean> {
  // Super admins always see gated/rolling-out features — lets an admin
  // build against and preview something before flipping it on for everyone,
  // without needing a separate "preview" mechanism. Kill-switch style flags
  // (true = blocked, e.g. "*_coming_soon") are excluded: bypassing those to
  // true would wall admins out instead of letting them past the wall.
  // "hide_*" flags are kill-switch style too (true = hidden), so they are excluded as well — otherwise the owner
  // would always see the hidden thing gone no matter how the flag is set.
  if (role === "super_admin" && !featureName.endsWith("_coming_soon") && !featureName.startsWith("hide_")) return true

  const flags = await getFeatureFlags()
  const flag = flags.find((f) => f.name === featureName)

  if (!flag) return DEFAULT_FEATURE_FLAG_ENABLED[featureName] ?? false
  if (!flag.enabled) return false

  // Handle user-specific rollout
  if (flag.rollout_type === "user_list") {
    if (!userId) return false
    return flag.target_user_ids.includes(userId)
  }

  // Handle percentage-based rollout
  if (flag.rollout_type === "percentage") {
    if (flag.rollout_percentage >= 100) return true
    if (!userId) return false

    // Check if user is already assigned
    const supabase = createBrowserClient()
    const { data: assignment } = await supabase
      .from("feature_flag_assignments")
      .select("*")
      .eq("feature_flag_id", flag.id)
      .eq("user_id", userId)
      .single()

    if (assignment) return true

    // Use consistent hashing to determine if user should get feature
    const hash = simpleHash(userId + flag.id)
    const shouldAssign = hash % 100 < flag.rollout_percentage

    if (shouldAssign) {
      // Record assignment
      await supabase.from("feature_flag_assignments").insert({ feature_flag_id: flag.id, user_id: userId })
    }

    return shouldAssign
  }

  return false
}

function simpleHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return Math.abs(hash)
}

export async function logFeatureError(featureName: string, error: Error, userId?: string): Promise<void> {
  const flags = await getFeatureFlags()
  const flag = flags.find((f) => f.name === featureName)

  if (!flag) return

  const supabase = createBrowserClient()
  await supabase.from("feature_flag_errors").insert({
    feature_flag_id: flag.id,
    user_id: userId || null,
    error_message: error.message,
    error_stack: error.stack || null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    page_url: typeof window !== "undefined" ? window.location.href : null,
  })
}

// Hook for "hide_*" flags: returns true when the thing should be hidden. Stays hidden until the flag has loaded so
// a hidden item never flashes on screen first.
export function useHideFlag(featureName: string): boolean {
  const { enabled, loading } = useFeatureFlag(featureName)
  return loading || enabled
}

// Hook for using feature flags in components
export function useFeatureFlag(featureName: string) {
  const { user, profile } = useAuthStore()
  const [enabled, setEnabled] = React.useState(false)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    isFeatureEnabled(featureName, user?.id, profile?.role).then((result) => {
      setEnabled(result)
      setLoading(false)
    })
  }, [featureName, user?.id, profile?.role])

  return { enabled, loading }
}

// Add React import for the hook
import React from "react"
